import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label, StatusChip, cad, day, errMsg } from "@/components/sponsored/format";

const REASON = {
  program_on: () => "Switch the offer off first.",
  recent_activity: (r) => `Your store had a Cyannect referral or sale recently. You can ask for a refund from ${day(r.eligible_from)}.`,
  nothing_to_refund: () => "There is no unused card-funded balance to refund.",
  request_open: () => "A refund request is already being reviewed.",
};

function History({ rows }) {
  if (!rows.length) return null;
  return (
    <ul className="space-y-2 text-sm" data-testid="sc-refund-history">
      {rows.slice(0, 5).map((x) => (
        <li key={x.id} className="flex items-center justify-between gap-2" data-testid={`sc-refund-row-${x.id}`}>
          <span className="text-slate-600">{day(x.requested_at)} · {cad(x.amount_minor / 100)}{x.status === "REFUNDED" ? ` · ${cad(x.refunded_minor / 100)} back to card` : ""}</span>
          <StatusChip status={x.status} />
        </li>
      ))}
    </ul>
  );
}

function ConfirmRefund({ open, onOpenChange, amount, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="sc-refund-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Request a refund of {cad(amount)}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600" data-testid="sc-refund-confirm-copy">
              <p>{cad(amount)} is moved out of your available budget and held while a Cyannect admin reviews the request.</p>
              <p>If approved, the money goes back to the card you used for your top-ups. If it is declined, the held amount returns to your budget.</p>
              <p data-testid="sc-refund-confirm-timing">You can follow the review status on this page. After approval, card refunds usually take 5–10 business days to appear on your statement.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="sc-refund-confirm-cancel">Keep my budget</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="sc-refund-confirm-btn">Send refund request</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RefundPanel({ d, onChange }) {
  const r = d.refund;
  const open = d.open_refund_request;
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    api.get("/merchant/sponsored-cashback/refund-requests").then((x) => setRows(x.data.rows)).catch(() => setRows([]));
  }, [d]);
  const ask = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/sponsored-cashback/refund-requests");
      toast.success("Refund request sent. A Cyannect admin will review it.");
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Refund isn't available yet"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/60 p-4" data-testid="sc-refund-panel">
      <Label>Refund unused budget</Label>
      <p className="text-sm text-slate-700" data-testid="sc-refund-policy">
        Your Sponsored Cashback Budget is refundable only if there have been no Cyannect referrals or sales for your store in the last {d.settings.refund_quiet_days} days.
        After that wait, request a refund. A Cyannect admin reviews it, and the approved amount goes back to the card you paid with.
      </p>
      {open ? (
        <div className="flex items-center justify-between text-sm" data-testid="sc-refund-open">
          <span>Refund of {cad(open.amount_minor / 100)} requested {day(open.requested_at)}</span><StatusChip status={open.status} />
        </div>
      ) : r.eligible ? (
        <button onClick={() => setConfirming(true)} disabled={busy} className="pf-btn-glass w-full px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="sc-refund-request-btn">
          {busy ? "Sending…" : `Request a refund of ${cad(r.refundable_cad)}`}
        </button>
      ) : (
        <ul className="text-xs text-slate-500 list-disc pl-4 space-y-1" data-testid="sc-refund-blocked">
          {r.reasons.map((k) => <li key={k} data-testid={`sc-refund-reason-${k}`}>{(REASON[k] || (() => k))(r)}</li>)}
        </ul>
      )}
      <ConfirmRefund open={confirming} onOpenChange={setConfirming} amount={r.refundable_cad} onConfirm={ask} />
      <History rows={rows} />
    </div>
  );
}
